import { useEffect, useRef, useState } from 'react';
import {
  createEmbeddingContext,
  EmbeddingEvents,
  EventListener,
  MessageEventName,
  QuickChatExperience,
} from 'amazon-quicksight-embedding-sdk';

import { sendCustomEvent, sendErrorEvent } from '@Analytics';
import { ErrorMessage } from '@Components';
import { Container, QnAContainer } from './askBops.styles';

interface AskBopsProps {
  embedUrl: string;
  agentId: string;
}

const FRAME_ID = 'amazon-q-frame';

const AskBops = ({ embedUrl, agentId }: AskBopsProps) => {
  const [embedError, setEmbedError] = useState<string>('');
  const experienceRef = useRef<QuickChatExperience | null>(null);
  const containerDiv = useRef<HTMLDivElement>(null);
  const hasRun = useRef(false);

  const cleanContainer = () => {
    const node = containerDiv.current;
    if (!node) return;
    while (node.firstChild) node.removeChild(node.firstChild);
  };

  useEffect(() => {
    const buildExperience = async () => {
      const onMessageHandler: EventListener = (event: EmbeddingEvents) => {
        switch (event.eventName) {
          case MessageEventName.ERROR_OCCURRED:
            sendErrorEvent(`Amazon Q error: ${event.message?.errorCode}`);
            break;
          default:
            break;
        }
      };

      const embeddingContext = await createEmbeddingContext();
      cleanContainer();

      const experience = await embeddingContext.embedQuickChat(
        {
          url: embedUrl,
          container: `#${FRAME_ID}`,
          withIframePlaceholder: true,
        },
        {
          // Pin the chat agent for the session; this also hides the agent selector.
          agentOptions: { fixedAgentId: agentId },
          // Keep the experience to "text + result" only — everything that
          // exposes internals (data sources, web search, history) is hidden.
          promptOptions: {
            allowFileAttachments: false,
            showAgentKnowledgeBoundary: false,
            showWebSearch: false,
            showChatHistory: false,
          },
          footerOptions: {
            showBrandAttribution: false,
            showUsagePolicy: false,
          },
          onMessage: onMessageHandler,
        },
      );

      experienceRef.current = experience;
      sendCustomEvent('AMAZON_Q_OPENED');
    };

    if (!hasRun.current) {
      hasRun.current = true;
      buildExperience().catch((e) => setEmbedError(String(e)));
    }
  }, [embedUrl, agentId]);

  if (embedError) return <ErrorMessage internalMessage={`Error loading Amazon Q: ${embedError}`} />;

  return (
    <Container>
      <QnAContainer id={FRAME_ID} ref={containerDiv} />
    </Container>
  );
};

export default AskBops;
